import { useState } from "react";

import PermissionsSelection from "common/components/PermissionsSelection";
import { Button } from "common/components/ui/Button";
import { DialogDescription, DialogTitle } from "common/components/ui/Dialog";
import { DrawerDescription, DrawerTitle } from "common/components/ui/Drawer";
import { USER_PERMISSIONS_CATEGORIES } from "common/constants";
import { typedObjectKeys } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { UserPermissions } from "common/types";

import useMembersMutations from "../../datahooks/useMembersMutations";

interface ChangeRoleDialogProps {
  currentPermissions: UserPermissions;
  memberId: string;
  email: string;
  onClose: () => void;
}

export default function UpdatePermissions({
  currentPermissions,
  memberId,
  email,
  onClose,
}: ChangeRoleDialogProps) {
  const isDesktop = useTwBreakpoint("lg");
  const [permissions, setPermissions] =
    useState<UserPermissions>(currentPermissions);

  const { changeMemberRole, isChangingRole } = useMembersMutations();

  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;

  const handleSave = () => {
    const isAdmin = typedObjectKeys(USER_PERMISSIONS_CATEGORIES).every((key) =>
      permissions.includes(`${key}_full`),
    );
    changeMemberRole({
      memberId,
      permissions,
      role: isAdmin ? "admin" : "member",
    }).then(onClose);
  };

  return (
    <>
      <Title>Update permissions for {email}</Title>
      <Description className="mb-8">
        Give this member full access to the workspace, or limit which pages they
        can manage
      </Description>

      <PermissionsSelection
        permissions={permissions}
        setPermissions={setPermissions}
      />

      <div className="mt-8 flex gap-x-2.5 justify-between">
        <Button
          className="lg:flex-1"
          disabled={isChangingRole}
          variant="secondary-black"
          onClick={onClose}
        >
          Cancel
        </Button>

        <Button
          className="lg:flex-1"
          isLoading={isChangingRole}
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
    </>
  );
}
